import React, { useState, useEffect } from "react";
import styles from "./Home.module.css";
import Header from "../../components/Header/Header";
import Tabs from "../../components/Tabs/Tabs";
import Search from "../../components/Search/Search";
import Slider from "../../components/Slider/Slider";
import Marquee from "../../components/Marquee/Marquee";
import TabContent from "../../components/TabContent/TabContent";
import { useApp } from "../../contexts/AppContext";
import LowerSection from "../../components/LowerSection/LowerSection";
import { t } from "../../utils/localization";
import dts from "../../assets/dts.svg";
import f from "../../assets/f.png";
import visually_impaired from "../../assets/visually_impaired.svg";
import pattern from "../../assets/pattern.svg";
import logo from "../../assets/logo.svg";

export default function Home() {
  const {
    state,
    setSelectedShop,
    setActiveTabId,
    setLanguage,
    setAccessibilityMode,
    setSelectedEvent,
  } = useApp();
  const activeTab = state.activeTabId;
  const [tabChangeTrigger, setTabChangeTrigger] = useState(0);
  const [isAccessibilityMode, setIsAccessibilityMode] = useState(false);
  const [isExitingAccessibilityMode, setIsExitingAccessibilityMode] =
    useState(false);

  // Sync local accessibility mode state with global state
  useEffect(() => {
    if (state.isAccessibilityMode !== undefined) {
      setIsAccessibilityMode(state.isAccessibilityMode);
    }
  }, [state.isAccessibilityMode]);

  // Hide scrollbars on home page but allow scrolling with mouse wheel
  useEffect(() => {
    const root = document.getElementById("root");
    const html = document.documentElement;
    const body = document.body;

    if (root) {
      root.style.overflow = "auto";
      root.style.setProperty("scrollbar-width", "none");
      root.style.setProperty("-ms-overflow-style", "none");
    }

    if (html) {
      html.style.overflow = "auto";
      html.style.setProperty("scrollbar-width", "none");
      html.style.setProperty("-ms-overflow-style", "none");
    }

    if (body) {
      body.style.overflow = "auto";
      body.style.setProperty("scrollbar-width", "none");
      body.style.setProperty("-ms-overflow-style", "none");
    }

    // Add global styles for webkit browsers
    const style = document.createElement("style");
    style.id = "home-scrollbar-hide";
    style.textContent = `
      #root::-webkit-scrollbar,
      html::-webkit-scrollbar,
      body::-webkit-scrollbar {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (root) {
        root.style.overflow = "";
        root.style.removeProperty("scrollbar-width");
        root.style.removeProperty("-ms-overflow-style");
      }
      if (html) {
        html.style.overflow = "";
        html.style.removeProperty("scrollbar-width");
        html.style.removeProperty("-ms-overflow-style");
      }
      if (body) {
        body.style.overflow = "";
        body.style.removeProperty("scrollbar-width");
        body.style.removeProperty("-ms-overflow-style");
      }
      const styleElement = document.getElementById("home-scrollbar-hide");
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  const getActiveTabName = () => {
    if (!activeTab) return "";
    const tab = state.tabs?.find((t) => t.id === activeTab);
    if (!tab) return "";
    if (state.language === "en") {
      return tab.name?.en || tab.name?.ru || "";
    }
    return tab.name?.ru || tab.name?.en || "";
  };

  const handleTabClick = (tabId: string | null) => {
    if (!tabId) return;

    // Check if the clicked tab is the Events tab
    const clickedTab = state.tabs?.find((t) => t.id === tabId);
    const isEventsTab = clickedTab
      ? (clickedTab.name?.ru?.toLowerCase?.() || "").includes("событ") ||
        (clickedTab.name?.en?.toLowerCase?.() || "").includes("event")
      : false;

    // If clicking on events tab (same or different), reset selected event
    if (isEventsTab) {
      setSelectedEvent(undefined);
    }

    // Don't reset selected shop when switching to map tab (id=1)
    // This preserves activeRoutePoints when switching to the map
    const tabIdNumber = parseInt(tabId);
    if (tabIdNumber !== 1) {
      setSelectedShop(undefined);
    }

    // If clicking on the same tab, toggle the trigger to force reset
    if (activeTab === tabId) {
      setTabChangeTrigger((prev) => prev + 1);
    }
    setActiveTabId(tabId);
  };

  const LowerSectionAny = LowerSection as unknown as React.ComponentType<any>;

  return (
    <div className={styles.homeContainerWrapper}>
      <div
        className={`${styles.homeContainer} ${
          isAccessibilityMode ? styles.accessibilityMode : ""
        } ${isExitingAccessibilityMode ? styles.exitingAccessibilityMode : ""}`}
      >
        <Header />
        <section className={styles.bannerSection}>
          <Slider />
        </section>
        <div className={styles.searchWrapper}>
          <Search />
          <div className={styles.languageSwitcher}>
            <button
              className={state.language === "ru" ? styles.active : ""}
              onClick={() => setLanguage("ru")}
            >
              RU
            </button>
            <button
              className={state.language === "en" ? styles.active : ""}
              onClick={() => setLanguage("en")}
            >
              EN
            </button>
          </div>
        </div>
        <div className={styles.tabsWrapper}>
          <Tabs activeTab={activeTab} onTabChange={handleTabClick} />
        </div>

        <div className={styles.contentSection}>
          {activeTab && (
            <TabContent
              key={`${activeTab}-${tabChangeTrigger}`}
              activeTabId={activeTab}
              tabName={getActiveTabName() || ""}
              onTabChange={handleTabClick}
            />
          )}
        </div>

        <div className={styles.marqueeWrapper}>
          <Marquee />
        </div>

        <div className={styles.lowerSectionWrapper}>
          <LowerSectionAny />
        </div>
        <div
          className={styles.footerContentAccessibility}
          // onClick={() => setIsAccessibilityMode(!isAccessibilityMode)}
        >
          {/* <img src={visually_impaired} alt="visually_impaired" /> */}
        </div>
        <footer className={styles.footer}>
          <div className={styles.footerContent}>
            <img src={pattern} alt="dts" />
          </div>
          <div className={styles.footerDeveloperSection}>
            <div className={styles.footerDeveloperText}>
              {t("developedByCompany", state.language)}
            </div>
            <div className={styles.footerDeveloperLogo}>
              <img src={logo} alt="logo" />
            </div>
            <div className={styles.footerDeveloperCompany}>High-tech.by</div>
          </div>
        </footer>
      </div>
      <div
        className={styles.footerContentAccessibilityButton}
        onClick={() => {
          if (isAccessibilityMode) {
            setIsAccessibilityMode(false);
            setAccessibilityMode(false);
            setIsExitingAccessibilityMode(true);
            setTimeout(() => {
              setIsExitingAccessibilityMode(false);
            }, 300);
          } else {
            setIsAccessibilityMode(true);
            setAccessibilityMode(true);
            setIsExitingAccessibilityMode(false);
          }
        }}
      >
        <img src={visually_impaired} alt="visually_impaired" />
      </div>
    </div>
  );
}
